// ABOUTME: Displays comprehensive family analytics dashboard with charts and metrics
// ABOUTME: Includes journey heatmap, behavior breakdown, engagement trends, goal progress, and time patterns

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import TimePatternsChart from '../TimePatternsChart';
import { GoalStatus } from '../../types/api';
import type { HourlyBehaviorAverage } from '../../types/analytics';
import type { FamilyAnalytics, FamilyDetailData } from './types';

interface FamilyAnalyticsSectionProps {
  analytics: FamilyAnalytics | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  behaviorPatterns: { hourlyAverages?: HourlyBehaviorAverage[] } | null;
  family: FamilyDetailData;
}

const FamilyAnalyticsSection: React.FC<FamilyAnalyticsSectionProps> = ({
  analytics,
  analyticsLoading,
  analyticsError,
  behaviorPatterns,
  family
}) => {
  const theme = useTheme();
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Family Analytics & Insights (Last 30 Days)
        </Typography>

        {analyticsLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : analyticsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {analyticsError}
          </Alert>
        ) : analytics ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Family Journey Heatmap */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📊 Family Journey Heatmap
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={analytics.journeyHeatmap.heatmapData.slice(-30)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="dayOfWeek"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => [`${value} behaviors`, 'Daily Total']}
                      labelFormatter={(label, payload) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return `${data.dayOfWeek || data.DayOfWeek} - ${data.date}`;
                        }
                        return label;
                      }}
                    />
                    <Bar
                      dataKey="totalBehaviors"
                      fill={theme.palette.info.main}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Daily max: {analytics.journeyHeatmap.maxBehaviorsPerDay} behaviors •
                  Average: {analytics.journeyHeatmap.averageDaily} per day
                </Typography>
              </CardContent>
            </Card>

            {/* Behavior Type Breakdown */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎯 Behavior Type Breakdown
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Positive', value: analytics.behaviorBreakdown.positiveBehaviors, color: theme.palette.success.main },
                        { name: 'Warning', value: analytics.behaviorBreakdown.warningBehaviors, color: theme.palette.warning.main },
                        { name: 'Consequence', value: analytics.behaviorBreakdown.consequenceBehaviors, color: theme.palette.error.main }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {[
                        { name: 'Positive', value: analytics.behaviorBreakdown.positiveBehaviors, color: theme.palette.success.main },
                        { name: 'Warning', value: analytics.behaviorBreakdown.warningBehaviors, color: theme.palette.warning.main },
                        { name: 'Consequence', value: analytics.behaviorBreakdown.consequenceBehaviors, color: theme.palette.error.main }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} (${
                      name === 'Positive' ? analytics.behaviorBreakdown.positivePercentage :
                      name === 'Warning' ? analytics.behaviorBreakdown.warningPercentage :
                      analytics.behaviorBreakdown.consequencePercentage
                    }%)`, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: theme.palette.success.main, borderRadius: '50%' }} />
                    <Typography variant="caption">Positive ({analytics.behaviorBreakdown.positivePercentage}%)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: theme.palette.warning.main, borderRadius: '50%' }} />
                    <Typography variant="caption">Warning ({analytics.behaviorBreakdown.warningPercentage}%)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: theme.palette.error.main, borderRadius: '50%' }} />
                    <Typography variant="caption">Consequence ({analytics.behaviorBreakdown.consequencePercentage}%)</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Daily Engagement Trends */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📈 Daily Engagement Trends
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={analytics.dailyEngagement.slice(-30)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="dayOfWeek"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name) => [
                        `${value}${name === 'avgBehaviorsPerChild' ? ' avg/child' : ''}`,
                        name === 'avgBehaviorsPerChild' ? 'Avg Behaviors/Child' : 'Total Behaviors'
                      ]}
                      labelFormatter={(label, payload) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return `${data.dayOfWeek || data.DayOfWeek} - ${data.date}`;
                        }
                        return label;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="avgBehaviorsPerChild"
                      stroke={theme.palette.info.main}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="avgBehaviorsPerChild"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Target: 15+ behaviors per child per day for optimal engagement
                </Typography>
              </CardContent>
            </Card>

            {/* Goal Progress */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏆 Goal Achievement Progress
                </Typography>
                {analytics.goalProgress.goals.length > 0 ? (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {analytics.goalProgress.activeGoals}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active Goals
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {analytics.goalProgress.completedGoals}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Completed
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {analytics.goalProgress.goals.map((goal) => (
                        <Box key={goal.goalId} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {goal.title} ({goal.childName})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {goal.completionPercentage}%
                            </Typography>
                          </Box>
                          <Box sx={{
                            width: '100%',
                            height: 6,
                            bgcolor: 'grey.200',
                            borderRadius: 3,
                            overflow: 'hidden'
                          }}>
                            <Box sx={{
                              width: `${Math.min(goal.completionPercentage, 100)}%`,
                              height: '100%',
                              bgcolor: (goal.status === GoalStatus.Completed || goal.status === GoalStatus.Celebrated) ? 'success.main' : 'primary.main',
                              transition: 'width 0.3s ease'
                            }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No active goals for this family
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Behavior Patterns by Time - Full Width */}
            <Card variant="outlined" sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ⏰ Behavior Patterns by Time
                </Typography>
                <TimePatternsChart
                  hourlyAveragesData={behaviorPatterns?.hourlyAverages}
                  familyTimezone={family?.timezone}
                  familyId={family?.id}
                />
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No analytics data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyAnalyticsSection;
